import { NextSeo } from "next-seo";
import { useState } from "react";
import { useRouter } from "next/router";
import { doc, getDoc } from "firebase/firestore";
import { normalizeDate } from "utils/dateUtils";
import { useFavicon } from "react-use";

import { db } from "utils/firebase";
import { BASE_URL } from "constants/redirects";
import ExpandedPost from "components/Post/ExpandedPost";
import { Layout } from "components/common";

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export async function getStaticProps(context) {
  const { params } = context;
  const id = params.postId;

  const post = await getDoc(doc(db, "posts", id || "0"));
  const postData = post?.data() || {};
  const postAuthor = await getDoc(
    doc(db, "publicUsers", postData?.authorId || "0")
  );
  const postAuthorData = postAuthor?.data() || {};

  const serializedPostData = JSON.parse(JSON.stringify(postData));
  const serializedPostAuthorData = JSON.parse(JSON.stringify(postAuthorData));

  return {
    props: {
      postData: serializedPostData,
      postAuthorData: serializedPostAuthorData,
    },
  };
}

function ProfileExpandedPost({ postData, postAuthorData }) {
  const { query } = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const origin =
    typeof window !== "undefined" && window?.location?.origin
      ? window.location.origin
      : BASE_URL;

  // Don't show details if post is archived
  const post = postData?.isArchived ? null : postData;
  const postAuthor = postData?.isArchived ? null : postAuthorData;

  useFavicon(postData?.customFaviconUrl || null);

  const getMetaTitle = () => {
    if (!post) return "Post not found";

    if (post?.shortTextContent) {
      if (postAuthor?.name)
        return `${post?.shortTextContent} - ${postAuthor?.name}`;

      return post?.shortTextContent;
    }

    if (post?.articleTitle) {
      if (postAuthor?.name)
        return `${post?.articleTitle} - ${postAuthor?.name}`;

      return post?.articleTitle;
    }

    const contentType =
      post?.type?.filter((item) => item !== "text" && item !== "short") || "";

    if (postAuthor?.name) return `${postAuthor?.name}'s ${contentType} post`;

    return "Starlo post";
  };

  const getMetaDescription = () => {
    if (!post) return "Post not found";

    if (post?.shortTextContent) {
      if (postAuthor?.name)
        return `${post?.shortTextContent} - ${postAuthor?.name}`;

      return post?.shortTextContent;
    }

    if (post?.articleTitle) {
      if (postAuthor?.name)
        return `${post?.articleTitle} - ${postAuthor?.name}`;

      return post?.articleTitle;
    }

    if (postAuthor?.name) return `Check out ${postAuthor?.name}'s post`;

    return "Check out post";
  };

  const getOpenGraphMetaTags = () => {
    if (!post) return null;

    const tags = [postAuthor?.name];

    if (post?.shortTextContent) {
      tags.push(...post?.shortTextContent?.split(" "));
    } else if (post?.articleTitle) {
      tags.push(...post?.articleTitle?.split(" "));
    }

    if (post?.type === "audio")
      return {
        audio: [
          {
            url: post?.audioUrl,
            secureUrl: post?.audioUrl,
            type: "audio/mpeg",
          },
        ],
      };

    if (post?.type === "video")
      return {
        type: "video.movie",
        video: {
          releaseDate: normalizeDate(post?.createdAt).toISOString(),
          tags,
        },
      };

    if (post?.type === "article")
      return {
        type: "article",
        article: {
          publishedTime: normalizeDate(post?.createdAt).toISOString(),
          modifiedTime: normalizeDate(post?.updatedAt).toISOString(),
          authors: [postAuthor?.name],
          section: post?.category,
          tags,
        },
      };
  };

  const getTwitterMetaTags = () => {
    if (!post) return null;

    if (post?.type === "audio" || post?.type === "video")
      return {
        twitter: { cardType: "player", site: "@starlo" },
      };
  };

  const SEO = {
    title: `${getMetaTitle()}`,
    description: `${getMetaDescription()}`,
    canonical: `${origin}/${postData?.id || ""}`,
    openGraph: {
      title: `${getMetaTitle()}`,
      description: `${getMetaDescription()}`,
      images: [
        {
          url: `${post?.imageSrc || `${BASE_URL}/starlo-blank-meta.jpg`}`,
          width: 1200,
          height: 627,
          alt: `${post?.shortTextContent || "Starlo image"}`,
        },
      ],
      ...getOpenGraphMetaTags(),
      ...getTwitterMetaTags(),
    },
  };

  return (
    <Layout hideNav>
      <NextSeo {...SEO} />

      <ExpandedPost
        backHref={`/`}
        postData={post}
        postAuthorData={postAuthor}
      />
    </Layout>
  );
}

export default ProfileExpandedPost;
